import { useCallback, useEffect, useRef, useState } from 'react';

export type RoomViewport = { zoom: number; panX: number; panY: number };

export type RoomState = {
  datasetId: string;
  cursorTs: string | null;
  playing: boolean;
  speed: number;
  viewport: RoomViewport;
  selectedChannel: string | null;
};

export type RoomPatch = {
  cursorTs?: string | null;
  playing?: boolean;
  speed?: number;
  selectedChannel?: string | null;
  viewport?: Partial<RoomViewport>;
};

export type RoomStatus = 'idle' | 'connecting' | 'open' | 'closed';

const HOST_TOKEN_PREFIX = 'ccs.host.';

export function loadHostToken(code: string): string | null {
  try {
    return window.localStorage.getItem(HOST_TOKEN_PREFIX + code);
  } catch {
    return null;
  }
}

export function saveHostToken(code: string, token: string): void {
  try {
    window.localStorage.setItem(HOST_TOKEN_PREFIX + code, token);
  } catch {
    // storage may be blocked; the host can still control while this tab stays open
  }
}

export function clearHostToken(code: string): void {
  try {
    window.localStorage.removeItem(HOST_TOKEN_PREFIX + code);
  } catch {
    // ignore
  }
}

type RoomMessage =
  | { type: 'room_state'; state: RoomState; hostOnline: boolean; hostName: string | null; isHost?: boolean }
  | { type: 'tick'; cursorTs: string; playing: boolean; speed: number }
  | { type: 'room_closed'; reason?: string };

export function useRoomSession(code: string | null, hostToken: string | null) {
  const [status, setStatus] = useState<RoomStatus>(code ? 'connecting' : 'idle');
  const [isHost, setIsHost] = useState(false);
  const [state, setState] = useState<RoomState | null>(null);
  const [hostOnline, setHostOnline] = useState(false);
  const [hostName, setHostName] = useState<string | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const closedRef = useRef(false);

  useEffect(() => {
    if (!code) {
      setStatus('idle');
      setIsHost(false);
      setState(null);
      setHostOnline(false);
      setHostName(null);
      return;
    }

    closedRef.current = false;
    setStatus('connecting');
    let attempts = 0;
    let socket: WebSocket | null = null;
    let timer: number | null = null;

    const connect = () => {
      if (closedRef.current) return;
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const token = hostToken ? `?hostToken=${encodeURIComponent(hostToken)}` : '';
      socket = new WebSocket(
        `${proto}://${window.location.host}/ws/rooms/${encodeURIComponent(code)}${token}`,
      );
      socketRef.current = socket;

      socket.onopen = () => {
        attempts = 0;
        setStatus('open');
      };

      socket.onmessage = (event) => {
        let message: RoomMessage;
        try {
          message = JSON.parse(event.data) as RoomMessage;
        } catch {
          return;
        }
        if (message.type === 'room_state') {
          setState(message.state);
          setHostOnline(Boolean(message.hostOnline));
          setHostName(message.hostName ?? null);
          if (typeof message.isHost === 'boolean') setIsHost(message.isHost);
        } else if (message.type === 'tick') {
          setState((prev) =>
            prev
              ? { ...prev, cursorTs: message.cursorTs, playing: message.playing, speed: message.speed }
              : prev,
          );
        } else if (message.type === 'room_closed') {
          closedRef.current = true;
          setStatus('closed');
          socket?.close();
        }
      };

      socket.onclose = () => {
        if (closedRef.current) return;
        setStatus('connecting');
        const delay = Math.min(5000, 500 * 2 ** attempts);
        attempts += 1;
        timer = window.setTimeout(connect, delay);
      };

      socket.onerror = () => {
        socket?.close();
      };
    };

    connect();

    return () => {
      closedRef.current = true;
      if (timer !== null) window.clearTimeout(timer);
      socket?.close();
      socketRef.current = null;
    };
  }, [code, hostToken]);

  const send = useCallback((message: unknown) => {
    const socket = socketRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
  }, []);

  const sendPatch = useCallback(
    (patch: RoomPatch) => send({ type: 'host_state', patch }),
    [send],
  );

  const endRoom = useCallback(() => {
    send({ type: 'end_room' });
    closedRef.current = true;
    setStatus('closed');
    socketRef.current?.close();
  }, [send]);

  return { status, isHost, state, hostOnline, hostName, sendPatch, endRoom };
}